/**
 * VolumeControl — 스피커 아이콘 + 미니 볼륨 bar.
 * Figma 585:506 기준: 24×24 아이콘 + 40×6 bar (gray/200 track, gray/700 fill).
 */
import { useCallback, useRef } from 'react'
import { Volume2, VolumeX } from 'lucide-react'

interface Props {
  volume: number
  isMuted: boolean
  onToggleMute: () => void
  onChangeVolume: (v: number) => void
  compact?: boolean
}

export default function VolumeControl({
  volume,
  isMuted,
  onToggleMute,
  onChangeVolume,
  compact = false,
}: Props) {
  const barRef = useRef<HTMLDivElement>(null)
  const displayFillPercent = isMuted ? 0 : volume * 100

  const handleBarPointer = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const bar = barRef.current
    if (!bar) return
    const rect = bar.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    onChangeVolume(ratio)
  }, [onChangeVolume])

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onToggleMute}
        className={
          compact
            ? 'flex size-6 items-center justify-center text-lc-v3-gray-600 hover:text-lc-v3-gray-800'
            : 'flex size-6 items-center justify-center text-lc-gray-700 hover:text-lc-gray-900'
        }
        aria-label={isMuted ? '음소거 해제' : '음소거'}
      >
        {isMuted ? <VolumeX className="size-6" /> : <Volume2 className="size-6" />}
      </button>

      {!compact ? (
        <div
          ref={barRef}
          className="relative w-10 h-[6px] bg-lc-gray-200 rounded-[50px] cursor-pointer"
          onPointerDown={handleBarPointer}
          role="slider"
          aria-label="볼륨"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(displayFillPercent)}
        >
          <div
            className="absolute left-0 top-0 h-full bg-lc-gray-700 rounded-[50px]"
            style={{ width: `${displayFillPercent}%` }}
          />
        </div>
      ) : null}
    </div>
  )
}
